import { client } from "./client";

import { userMutations } from "~apollo/graphql-gateway/queries/userQueries";
import {
  hasAlreadyExistsErr,
  hasNotFoundErr,
  logout
} from "~lib/authentication";
import { captchaContext } from "~lib/captcha";
import { ResponseObj } from "~lib/commonType/apollo";
import {
  FormData,
  InterviewDecision,
  InterviewSource,
  InterviewSubmissionPayload,
  InterviewTestType
} from "~lib/commonType/interview";
import { emailValidator } from "~lib/validators";

const {
  guestVerify,
  createGuestUser: createGuest,
  updateUserInfo,
  requestEmailChange: requestEmailChangeMutation,
  confirmEmailChange: confirmEmailChangeMutation,
  deleteUser,
  getUserInfo,
  confirmEmailNotExist,
  connectOtherUserAccounts,
  initIndeedOAuth,
  signInWithIndeed,
  googleLogin,
  appleLogin,
  connectUserAccount,
  connectWithIndeed,
  contactUs,
  ShareResumeToIndeed,
  getIndeedProfile,
  submitInterviewForm,
  cancelInterviewForm,
  setInterviewEmailSubscription,
  getInterviewForm
} = userMutations;

const decodeHtmlEntities = (value: unknown): unknown => {
  if (typeof value !== "string") {
    return value;
  }

  if (typeof document === "undefined") {
    return value
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
  }

  const textarea = document.createElement("textarea");
  textarea.innerHTML = value;
  return textarea.value;
};

const decodeInterviewFormData = (
  formData: FormData | null
): FormData | null => {
  if (!formData) {
    return null;
  }

  return (Object.fromEntries(
    Object.entries(formData).map(([key, value]) => [
      key,
      decodeHtmlEntities(value)
    ])
  ) as unknown) as FormData;
};

export const confirmEmailDoesNotExist = async (
  accountEmail: string
): Promise<ResponseObj> => {
  const response: ResponseObj = { success: false, error: null };

  if (accountEmail && emailValidator(accountEmail)) {
    try {
      const { data } = await client.mutate({
        mutation: confirmEmailNotExist,
        variables: {
          accountEmail
        }
      });

      if ((((data || {}).User || {}).confirmEmailDoesNotExist || {}).message) {
        response.success = true;
        return response;
      }
      return null;
    } catch (e) {
      if (hasAlreadyExistsErr(e)) {
        response.error = "ALREADY_EXISTS";
      } else {
        response.error = "UNKNOWN";
      }
      return response;
    }
  } else {
    response.error = "EMPTY";
    return response;
  }
};

export const verifyGuestUser = async (
  accountEmail: string,
  firstName: string,
  lastName: string
): Promise<ResponseObj> => {
  const response: ResponseObj = { success: false, error: null };

  if (accountEmail && firstName && lastName) {
    try {
      const { data } = await client.mutate({
        mutation: guestVerify,
        variables: {
          accountEmail,
          firstName,
          lastName
        }
      });
      const userData = data && data.User && data.User.guestVerify;

      response.data = userData;
      response.success = !!userData;

      return response;
    } catch (e) {
      console.log("verifyGuestUser error", e);
      if (hasNotFoundErr(e)) {
        response.error = "NOT_FOUND";
      } else if (hasAlreadyExistsErr(e)) {
        response.error = "ALREADY_EXISTS";
      } else {
        response.error = e;
      }
      return response;
    }
  } else {
    response.error = "EMPTY";
    return response;
  }
};

export const createGuestUser = async (): Promise<ResponseObj> => {
  const response: ResponseObj = { success: false, error: null };

  try {
    const context = await captchaContext("createGuest");
    const { data } = await client.mutate({
      mutation: createGuest,
      context
    });

    if (data && data.User && data.User.createGuest) {
      const createGuestResponse = data.User.createGuest || {};

      let userId = "";
      if (createGuestResponse.user && createGuestResponse.user.id) {
        userId = createGuestResponse.user.id;
        response.user = createGuestResponse.user;
        // to maintenance both versions
        if (createGuestResponse.magicToken) {
          response.magicToken = createGuestResponse.magicToken;
        }
      } else if (createGuestResponse.id) {
        // to maintenance both versions
        userId = createGuestResponse.id;
        response.user = createGuestResponse;
      }
      if (userId) {
        response.success = true;
      }
    }

    return response;
  } catch (e) {
    response.error = "UNKNOWN";
    return response;
  }
};

export const updateUser = async (
  firstName: string,
  lastName: string
): Promise<ResponseObj> => {
  const response: ResponseObj = { success: false, error: null };
  try {
    const { data } = await client.mutate({
      mutation: updateUserInfo,
      variables: {
        firstName,
        lastName
      }
    });
    const userData = data && data.User && data.User.update;
    response.success = !!userData;
    response.data = userData;
    return response;
  } catch (e) {
    response.error = e;
    return response;
  }
};

export const requestEmailChange = async (
  accountEmail: string
): Promise<ResponseObj> => {
  const response: ResponseObj = { success: false, error: null };
  try {
    const context = await captchaContext("requestEmailChange");
    const { data } = await client.mutate({
      mutation: requestEmailChangeMutation,
      variables: {
        accountEmail
      },
      context
    });
    const result = data && data.User && data.User.requestEmailChange;
    response.success = !!result;
    response.data = result;
    return response;
  } catch (e) {
    response.error = e;
    return response;
  }
};

export const confirmEmailChangeAction = async (
  token: string
): Promise<ResponseObj> => {
  const response: ResponseObj = { success: false, error: null };
  try {
    const { data } = await client.mutate({
      mutation: confirmEmailChangeMutation,
      variables: {
        token
      }
    });
    const userData = data && data.User && data.User.confirmEmailChange;
    response.success = !!userData;
    response.data = userData;
    return response;
  } catch (e) {
    response.error = e;
    return response;
  }
};

export const removeUser = async (userID: string): Promise<ResponseObj> => {
  const response: ResponseObj = { success: false, error: null };
  try {
    await client.mutate({
      mutation: deleteUser,
      variables: {
        userID
      }
    });
    await logout();
    response.success = true;
    return response;
  } catch (e) {
    response.error = e;
    return response;
  }
};

export const getUser = async (userID: string): Promise<ResponseObj> => {
  const response: ResponseObj = { success: false, error: null };
  try {
    const { data } = await client.query({
      query: getUserInfo,
      variables: {
        id: userID
      }
    });
    const userData = data && data.User;
    response.success = !!userData;
    response.data = userData;
    return response;
  } catch (e) {
    response.error = e;
    return response;
  }
};

export const sendConnectAccountEmail = async (
  accountEmail: string,
  userID: string
): Promise<ResponseObj> => {
  const response: ResponseObj = { success: false, error: null };
  if (accountEmail && emailValidator(accountEmail)) {
    try {
      const { data } = await client.mutate({
        mutation: connectOtherUserAccounts,
        variables: {
          accountEmail,
          userID
        }
      });

      if ((((data || {}).User || {}).connectOtherUserAccounts || {}).message) {
        response.success = true;
        return response;
      }
      return null;
    } catch (e) {
      response.error = e;
      return response;
    }
  }
};

// CDPE-703: starts the "Sign in with Indeed" flow via the gateway, which
// generates the CSRF state + PKCE verifier, stores them in HttpOnly cookies
// bound to this session, and returns a server-built authorize URL. This is now
// the only way the web starts the flow -- CDPE-772 removed the client-built
// authorize URL that /login/mobile used, which carried no code_challenge and a
// Math.random() state. The app builds its own URL and runs PKCE on the device.
export const startIndeedOAuth = async (
  from?: string
): Promise<string | null> => {
  try {
    const { data } = await client.mutate({
      mutation: initIndeedOAuth,
      variables: { from }
    });
    return data?.User?.initIndeedOAuth?.authUrl || null;
  } catch (e) {
    return null;
  }
};

export const logInWithIndeed = async (
  code: string,
  state: string
): Promise<ResponseObj> => {
  const response: ResponseObj = {
    success: false,
    error: null,
    magicToken: null
  };
  if (code) {
    try {
      const { data } = await client.mutate({
        mutation: signInWithIndeed,
        variables: {
          code,
          state
        }
      });
      if (data?.User?.signInWithIndeed?.magicToken) {
        response.success = true;
        response.magicToken = data.User.signInWithIndeed.magicToken;
        response.indeedEmail = data.User.signInWithIndeed.indeedEmail;
        return response;
      }
      return response;
    } catch (e) {
      response.error = e;
      return response;
    }
  }
};

export const loginWithGoogle = async (
  credential: string
): Promise<ResponseObj> => {
  const response: ResponseObj = { success: false, error: null };
  if (credential) {
    try {
      const { data } = await client.mutate({
        mutation: googleLogin,
        variables: {
          idToken: credential,
          siwi: false
        }
      });
      if (data?.User?.googleLogin?.magicToken) {
        response.success = true;
        response.magicToken = data.User.googleLogin.magicToken;
      }
      return response;
    } catch (e) {
      response.error = e;
      return response;
    }
  }
};

export const mergeUserAccount = async (
  userID: string,
  connectToken: string
): Promise<ResponseObj> => {
  const response: ResponseObj = { success: false, user: null, error: null };

  if (connectToken) {
    try {
      const { data } = await client.mutate({
        mutation: connectUserAccount,
        variables: {
          userID,
          connectToken
        }
      });
      if (data?.User?.connectUserAccount?.id) {
        response.success = true;
        return response;
      }
      return response;
    } catch (e) {
      response.error = e;
      return response;
    }
  }
};

export const connectAccountWithIndeed = async (
  userID: string,
  code: string,
  state: string
): Promise<ResponseObj> => {
  const response: ResponseObj = { success: false, error: null };
  if (userID && code) {
    try {
      const { data } = await client.mutate({
        mutation: connectWithIndeed,
        variables: {
          userID,
          code,
          state
        }
      });
      if (data?.User?.connectWithIndeed?.message) {
        response.success = true;
        return response;
      }
      return response;
    } catch (e) {
      response.error = e;
      return response;
    }
  }
};

export const sendContactEmail = async (
  userEmail: string,
  fullName: string,
  subject: string,
  message: string
): Promise<ResponseObj> => {
  const response: ResponseObj = { success: false, error: null };
  try {
    await client.mutate({
      mutation: contactUs,
      variables: {
        userEmail,
        fullName,
        subject,
        message
      }
    });
    response.success = true;
    return response;
  } catch (e) {
    response.error = e;
    return response;
  }
};

export const shareWithIndeed = async (
  resumeId: string,
  isPrivate: boolean,
  sharedResumeHash: string
): Promise<ResponseObj> => {
  const response: ResponseObj = { success: false, error: null };
  try {
    await client.mutate({
      mutation: ShareResumeToIndeed,
      variables: {
        id: resumeId,
        appId: "RESCOM",
        isPrivate,
        sharedResumeHash
      }
    });
    response.success = true;
    return response;
  } catch (e) {
    response.error = e;
    return response;
  }
};

export const getIndeedProfileStatus = async (): Promise<ResponseObj> => {
  const response: ResponseObj = { success: false, error: null, profile: null };
  try {
    const { data } = await client.mutate({
      mutation: getIndeedProfile
    });

    if (data?.User?.getIndeedProfile?.profile?.resume?.id) {
      response.profile = data.User.getIndeedProfile.profile;
    }
    response.success = true;
    return response;
  } catch (e) {
    response.error = e;
    return response;
  }
};

export const logInWithApple = async (idToken: string): Promise<ResponseObj> => {
  const response: ResponseObj = {
    success: false,
    error: null,
    magicToken: null
  };
  if (idToken) {
    try {
      const { data } = await client.mutate({
        mutation: appleLogin,
        variables: {
          idToken: idToken,
          siwi: false
        }
      });
      if (data?.User?.appleLogin?.magicToken) {
        response.success = true;
        response.magicToken = data.User.appleLogin.magicToken;
      }
      return response;
    } catch (e) {
      response.error = e;
      return response;
    }
  }
};

export const submitInterviewFormData = async (
  formData: FormData,
  decision: InterviewDecision,
  source: InterviewSource,
  cohort?: string,
  aiawToken?: string
): Promise<ResponseObj> => {
  const response: ResponseObj = { success: false, error: null };

  try {
    const { data } = await client.mutate({
      mutation: submitInterviewForm,
      variables: {
        formDataJson: JSON.stringify(formData),
        decision,
        source,
        cohort,
        aiawToken
      }
    });

    const submission = data?.User?.submitInterviewForm;
    response.success = !!submission?.message;
    response.data = submission;
    return response;
  } catch (e) {
    response.error = e;
    return response;
  }
};

export const getInterviewFormData = async (): Promise<ResponseObj> => {
  const response: ResponseObj = { success: false, error: null };

  try {
    const { data } = await client.query({
      query: getInterviewForm,
      fetchPolicy: "network-only"
    });

    const payload = data?.User?.getInterviewForm;
    const parsedFormData = payload?.formDataJson
      ? (JSON.parse(payload.formDataJson) as FormData)
      : null;
    const interviewSubmissionPayload: InterviewSubmissionPayload = {
      formData: decodeInterviewFormData(parsedFormData),
      price: typeof payload?.price === "number" ? payload.price : null,
      decision: payload?.decision || "",
      isEmailUnsubscribed: !!payload?.isEmailUnsubscribed,
      testType: (payload?.testType as InterviewTestType) || null
    };
    response.success = true;
    response.data = interviewSubmissionPayload;
    return response;
  } catch (e) {
    response.error = e;
    return response;
  }
};

export const setInterviewEmailSubscriptionData = async (
  isEmailUnsubscribed: boolean
): Promise<ResponseObj> => {
  const response: ResponseObj = { success: false, error: null };

  try {
    const { data } = await client.mutate({
      mutation: setInterviewEmailSubscription,
      variables: {
        isEmailUnsubscribed
      }
    });

    const payload = data?.User?.setInterviewEmailSubscription;
    response.success = !!payload?.message;
    response.data = payload;
    return response;
  } catch (e) {
    response.error = e;
    return response;
  }
};

export const cancelInterviewFormData = async (): Promise<ResponseObj> => {
  const response: ResponseObj = { success: false, error: null };

  try {
    const { data } = await client.mutate({
      mutation: cancelInterviewForm
    });

    const payload = data?.User?.cancelInterviewForm;
    response.success = !!payload?.message;
    response.data = payload;
    return response;
  } catch (e) {
    response.error = e;
    return response;
  }
};
